import { InfoIcon, discordIcon, openLinkIcon } from "@/designSystemIcons";
import { mixpanelTrack } from "@/events/mixpanelTrack";
import { BREAKPOINTS, chromeExtensionUrl } from "@/settings";
import { Trans, useTranslation } from "@repo/shared/i18n";
import { ChevronDown } from "lucide-react";
import { type ReactNode, useEffect, useState } from "react";
import { Link } from "react-router";
import { useMediaQuery } from "usehooks-ts";

const TroubleshootingItem = ({
  title,
  children,
}: {
  title: string;
  children?: React.ReactNode;
}) => {
  const { t } = useTranslation("clubImport");
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="w-full border-b border-[#333a42] last:border-0">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex w-full items-center justify-between py-5 text-left group"
      >
        <span className="text-[16px] font-medium text-[#74c1f8]">{title}</span>
        <ChevronDown
          className={`h-6 w-6 text-[#74c1f8] transition-transform duration-200 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>
      {isOpen && (
        <div className="pb-5 text-[#d4d8de] text-sm">
          {children || <p>{t("troubleshoot.fallbackBody", { title })}</p>}
        </div>
      )}
    </div>
  );
};

/*
  Link shells for the Trans sentences below. Trans replaces a component's
  children with the translated text, so the trailing icon lives in the shell
  rather than in the element passed to `components`.
*/
const DiscordLink = ({ children }: { children?: ReactNode }) => (
  <a
    href="https://discord.gg/easysbc-980827940902293525"
    target="_blank"
    rel="noreferrer"
    className="text-[#74c1f8] hover:underline inline-flex items-center gap-1"
  >
    {children} <div className="w-3 h-3">{openLinkIcon}</div>
  </a>
);

const GuideLink = ({ children }: { children?: ReactNode }) => (
  <Link
    to="/club-import-tutorial"
    className="text-[#74c1f8] hover:underline inline-flex items-center gap-1"
  >
    {children} <div className="w-3 h-3">{openLinkIcon}</div>
  </Link>
);

const ImportTroubleshoot = () => {
  const { t } = useTranslation("clubImport");
  const isTouchDevice = useMediaQuery(BREAKPOINTS.TOUCH_DEVICE.query);

  useEffect(() => {
    mixpanelTrack("import_troubleshoot_viewed");
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#1a1e23] relative">
      <div className="mx-auto max-w-[1280px] px-8 py-12 lg:px-[80px]">
        {/* Header */}
        <div className="mb-12">
          <h1 className="mb-2 text-[32px] font-medium leading-tight text-white">
            {isTouchDevice
              ? t("troubleshoot.titleMobile")
              : t("troubleshoot.title")}
          </h1>
          <p className="text-[16px] text-[#d4d8de]">
            {t("troubleshoot.subtitle")}
          </p>
        </div>

        <div className="flex flex-col gap-10 lg:w-[729px]">
          {/* Main Troubleshooting Section */}
          <div className="w-full rounded-[20px] border border-[#333a42] bg-[#282d34] p-[50px] max-touch-device:p-4">
            <h2 className="mb-4 text-[28px] max-touch-device:text-[22px] font-medium leading-tight text-white">
              {t("troubleshoot.main.title")}
            </h2>
            <p className="mb-9 text-[16px] text-[#d4d8de]">
              {t("troubleshoot.main.intro")}
            </p>

            <div className="flex flex-col">
              <div className="w-full border-t border-[#333a42]">
                <TroubleshootingItem
                  title={t("troubleshoot.items.browser.title")}
                >
                  <p>{t("troubleshoot.items.browser.body")}</p>
                </TroubleshootingItem>
                <TroubleshootingItem
                  title={t("troubleshoot.items.extension.title")}
                >
                  <p>
                    <Trans
                      ns="clubImport"
                      i18nKey="troubleshoot.items.extension.body"
                      components={{
                        textLink: (
                          <a
                            href={chromeExtensionUrl("troubleshoot")}
                            target="_blank"
                            rel="noreferrer"
                            className="underline"
                          />
                        ),
                      }}
                    />
                  </p>
                </TroubleshootingItem>
                <TroubleshootingItem
                  title={t("troubleshoot.items.cache.title")}
                >
                  <p>{t("troubleshoot.items.cache.body")}</p>
                </TroubleshootingItem>
                <TroubleshootingItem
                  title={t("troubleshoot.items.otherExtensions.title")}
                >
                  <p>{t("troubleshoot.items.otherExtensions.body")}</p>
                </TroubleshootingItem>
                <TroubleshootingItem
                  title={t("troubleshoot.items.clickThrough.title")}
                >
                  <p>{t("troubleshoot.items.clickThrough.body")}</p>
                </TroubleshootingItem>
              </div>
            </div>
          </div>

          {/* Still Stuck Section */}
          <div className="w-full rounded-[20px] border border-[#333a42] bg-[#282d34] p-[50px] max-touch-device:p-4">
            <h2 className="mb-9 text-[28px] max-touch-device:text-[22px] font-medium leading-tight text-white">
              {t("troubleshoot.stillStuck.title")}
            </h2>
            <div className="flex flex-col gap-6">
              {/* Discord Link */}
              <div className="flex items-start gap-4 border-b border-[#333a42] pb-6">
                <div className="my-auto shrink-0 text-white">{discordIcon}</div>
                <p className="text-[16px] leading-6 text-white">
                  <Trans
                    ns="clubImport"
                    i18nKey="troubleshoot.stillStuck.discord"
                    components={{ textLink: <DiscordLink /> }}
                  />
                </p>
              </div>

              {/* Import Guide Link */}
              <div className="flex items-start gap-4">
                <div className="my-auto shrink-0 text-white">
                  <InfoIcon outerFill="#9CA6B2" width="20" height="20" />
                </div>
                <p className="text-[16px] leading-6 text-white">
                  <Trans
                    ns="clubImport"
                    i18nKey="troubleshoot.stillStuck.guide"
                    components={{ textLink: <GuideLink /> }}
                  />
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImportTroubleshoot;
